import React, { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, ChevronUp, Paperclip, Trash2, Sparkles, PlusCircle } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUSES = [
  { key: "new", label: "New" },
  { key: "reviewing", label: "Reviewing" },
  { key: "planned", label: "Planned" },
  { key: "done", label: "Done" },
  { key: "declined", label: "Declined" },
];
const STATUS_CLS = {
  new: "bg-[#1E3F33]/10 text-[#5C6B64]",
  reviewing: "bg-[#B98900]/15 text-[#8a6800]",
  planned: "bg-[#2D6A4F]/15 text-[#2D6A4F]",
  done: "bg-[#2D6A4F] text-white",
  declined: "bg-[#B91C1C]/10 text-[#B91C1C]",
};

function AdminCard({ s, onPatch, onDelete }) {
  const [status, setStatus] = useState(s.status);
  const [reply, setReply] = useState(s.reply || "");
  return (
    <div className="card-soft flex gap-4" data-testid={`admin-suggestion-${s.id}`}>
      <div className="shrink-0 flex flex-col items-center justify-center w-14 rounded-xl border border-[#1E3F33]/15 text-[#5C6B64]">
        <ChevronUp className="w-5 h-5" strokeWidth={2.2} />
        <span className="font-heading text-lg leading-none">{s.votes}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-[#CC5A3A] bg-[#CC5A3A]/10 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
            {s.category === "service" ? <PlusCircle className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />} {s.category_label}
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_CLS[s.status]}`} data-testid={`admin-suggestion-status-${s.id}`}>{STATUSES.find((x) => x.key === s.status)?.label || s.status}</span>
          {s.priority && <span className="text-[10px] text-[#8A9A86] capitalize">· {s.priority} urgency</span>}
        </div>
        <h3 className="font-heading text-lg text-[#1E3F33] mt-1.5">{s.title}</h3>
        <p className="text-sm text-[#5C6B64] mt-1 whitespace-pre-wrap break-words">{s.description}</p>
        <div className="text-[11px] text-[#8A9A86] mt-1.5">
          By {s.author_name}{s.author_role ? ` (${s.author_role})` : ""}{s.author_email ? ` · ${s.author_email}` : ""} · {fmt(s.created_at)}
        </div>
        {(s.attachments || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {s.attachments.map((a, i) => (
              <a key={i} href={`${API}/files/${a.file_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#CC5A3A] hover:underline border border-[#1E3F33]/10 rounded-lg px-2.5 py-1.5">
                <Paperclip className="w-3.5 h-3.5" /> {a.original_name || `Attachment ${i + 1}`}
              </a>
            ))}
          </div>
        )}

        <div className="mt-4 border-t border-[#1E3F33]/10 pt-3 flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <select data-testid={`admin-suggestion-status-select-${s.id}`} value={status} onChange={(e) => setStatus(e.target.value)} className="input-soft sm:max-w-[180px] text-sm">
              {STATUSES.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
            </select>
            <input data-testid={`admin-suggestion-reply-${s.id}`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply shown publicly to everyone…" className="input-soft flex-1 text-sm" />
            <button data-testid={`admin-suggestion-save-${s.id}`} onClick={() => onPatch(s.id, { status, reply })} className="btn-primary text-xs py-2 px-4 shrink-0">Save</button>
          </div>
          <button data-testid={`admin-suggestion-delete-${s.id}`} onClick={() => onDelete(s.id)} className="self-start text-xs py-1 px-2 rounded-lg text-[#B91C1C] hover:bg-[#B91C1C]/5 inline-flex items-center gap-1.5">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSuggestions() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("top");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { sort };
    if (status !== "all") params.status = status;
    if (category !== "all") params.category = category;
    api.get("/admin/suggestions", { params })
      .then(({ data }) => { setItems(data.items); setCounts(data.counts || {}); setTotal(data.total || 0); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status, category, sort]); // eslint-disable-line

  const patch = async (id, body) => {
    try { await api.patch(`/admin/suggestions/${id}`, body); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this suggestion permanently?")) return;
    try { await api.delete(`/admin/suggestions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="container-x py-12" data-testid="admin-suggestions">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2"><Lightbulb className="w-6 h-6 text-[#CC5A3A]" /> Suggestions ({total})</h1>
          <p className="text-[#5C6B64] mt-2">Review user ideas and new service-type requests. Update status and reply — replies show publicly.</p>
        </div>
        <div className="text-right"><div className="text-xs uppercase tracking-wide text-[#8A9A86]">New</div><div className="font-heading text-2xl text-[#CC5A3A]" data-testid="admin-suggestions-new-count">{counts.new || 0}</div></div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button data-testid="admin-suggestions-status-all" onClick={() => setStatus("all")} className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${status === "all" ? "bg-[#1E3F33] text-white border-[#1E3F33]" : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s.key} data-testid={`admin-suggestions-status-${s.key}`} onClick={() => setStatus(s.key)} className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${status === s.key ? "bg-[#1E3F33] text-white border-[#1E3F33]" : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"}`}>
            {s.label} ({counts[s.key] || 0})
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <select data-testid="admin-suggestions-category" value={category} onChange={(e) => setCategory(e.target.value)} className="input-soft text-sm py-2">
            <option value="all">All categories</option>
            <option value="feature">Feature ideas</option>
            <option value="service">New service types</option>
          </select>
          <select data-testid="admin-suggestions-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input-soft text-sm py-2">
            <option value="top">Most upvoted</option>
            <option value="new">Newest</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : items.length === 0 ? (
        <p className="mt-8 text-sm text-[#8A9A86]" data-testid="admin-suggestions-empty">No suggestions match.</p>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((s) => <AdminCard key={s.id} s={s} onPatch={patch} onDelete={del} />)}
        </div>
      )}
    </div>
  );
}
